import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import classnames from "classnames";
import style from "./style.module.scss";
import type { PopoverProps, PopoverPlacement } from "../../types";

interface Position {
  top: number;
  left: number;
}

function getPositionForPlacement(rect: DOMRect, placement: PopoverPlacement, offset = 0): Position {
  switch (placement) {
    case "bottom-center":
      return {
        top: rect.bottom + offset,
        left: rect.left + rect.width / 2,
      };
    case "center":
      return {
        top: rect.top + rect.height / 2,
        left: rect.left + rect.width / 2,
      };
    default:
      return {
        top: rect.bottom + offset,
        left: rect.left + rect.width / 2,
      };
  }
}

function Popover({
  anchorRef,
  open,
  placement = "bottom-center",
  offset = 0,
  className,
  children,
}: PopoverProps) {
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (!anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    // Account for visual viewport offset (mobile keyboard)
    const visualViewportOffset = window.visualViewport?.offsetTop || 0;
    const pos = getPositionForPlacement(rect, placement, offset);
    setPosition({
      top: pos.top + visualViewportOffset,
      left: pos.left,
    });
  }, [anchorRef, placement, offset]);

  useEffect(() => {
    if (!open) return;

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    // Listen to visual viewport changes (mobile keyboard)
    window.visualViewport?.addEventListener("resize", updatePosition);
    window.visualViewport?.addEventListener("scroll", updatePosition);

    // Listen to anchor element size changes (e.g., textarea growing)
    let resizeObserver: ResizeObserver | undefined;
    if (anchorRef?.current) {
      resizeObserver = new ResizeObserver(updatePosition);
      resizeObserver.observe(anchorRef.current);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
      window.visualViewport?.removeEventListener("resize", updatePosition);
      window.visualViewport?.removeEventListener("scroll", updatePosition);
      resizeObserver?.disconnect();
    };
  }, [open, updatePosition, anchorRef]);

  if (!open) return null;

  return createPortal(
    <div
      className={classnames(style.popover, style[placement], className)}
      style={{ top: position.top, left: position.left }}
    >
      {children}
    </div>,
    document.body,
  );
}

export default Popover;
